import { useCallback, useEffect, useMemo, useState } from "react";

import api from "../services/api";

function toOptionLabel(option, currencySymbol) {
  const store =
    option.store_label ||
    (option.store_chain && option.store_name
      ? `${option.store_chain} – ${option.store_name}`
      : option.store_chain || option.store_name || "");

  const packageText =
    option.package_size_text ||
    (option.package_size_value && option.package_size_unit
      ? `${option.package_size_value} ${option.package_size_unit}`
      : "pkg");

  const packagePrice =
    typeof option.price === "number"
      ? `${currencySymbol}${option.price.toFixed(2)}`
      : `${currencySymbol}${option.price || "0.00"}`;

  const packsNeeded = option.packages_needed != null ? ` · ${option.packages_needed}x` : "";
  const packCost = option.pack_cost != null ? ` · ${currencySymbol}${Number(option.pack_cost).toFixed(2)}` : "";

  if (store && option.brand_name) {
    return `${store} – ${option.brand_name} (${packageText} · ${packagePrice}${packsNeeded}${packCost})`;
  }
  if (store) {
    return `${store} (${packageText} · ${packagePrice}${packsNeeded}${packCost})`;
  }
  if (option.brand_name) {
    return `${option.brand_name} (${packageText} · ${packagePrice}${packsNeeded}${packCost})`;
  }
  return `${packageText} · ${packagePrice}${packsNeeded}${packCost}`;
}

export function formatSelectedSelectionSummary(item, currencySymbol) {
  const selectedVariant = item?.selected_variant_snapshot || item?.selected_variant || {};
  const pricing = item?.pricing_snapshot || {};
  const brand =
    selectedVariant.brand_name_el ||
    selectedVariant.brand_name ||
    item?.brand_name ||
    "";
  const store =
    selectedVariant.store_chain && selectedVariant.store_name
      ? `${selectedVariant.store_chain} – ${selectedVariant.store_name}`
      : selectedVariant.store_chain || selectedVariant.store_name || item?.store || "";
  const packageText =
    pricing.package_size_text ||
    selectedVariant.package_size_text ||
    (pricing.package_size_value && pricing.package_size_unit
      ? `${pricing.package_size_value} ${pricing.package_size_unit}`
      : null);
  const price = pricing.package_price ?? selectedVariant.price ?? item?.price;
  return {
    brand,
    store,
    packageText,
    packagePriceLabel: price != null ? `${currencySymbol}${Number(price).toFixed(2)}` : null,
  };
}

export default function useShoppingItemSelection({
  item,
  currencySymbol,
  onItemUpdated,
  onSelectionSettled,
}) {
  const [genericProducts, setGenericProducts] = useState([]);
  const [variants, setVariants] = useState([]);
  const [loadingGenericProducts, setLoadingGenericProducts] = useState(false);
  const [loadingVariants, setLoadingVariants] = useState(false);
  const [submittingSelection, setSubmittingSelection] = useState(false);
  const [selectionError, setSelectionError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGenericId, setSelectedGenericId] = useState(item?.generic_product_id || "");
  const [selectionMode, setSelectionMode] = useState("genericThenVariants");
  const [lockedGenericProductId, setLockedGenericProductId] = useState(null);
  const [lockedGenericProductName, setLockedGenericProductName] = useState("");

  const selectedVariantId = item?.selected_variant_id || item?.product_variant_id || "";

  useEffect(() => {
    setSelectedGenericId(item?.generic_product_id || "");
  }, [item?.generic_product_id]);

  useEffect(() => {
    setLockedGenericProductId(item?.generic_product_id || null);
    setLockedGenericProductName(
      item?.generic_product_name_el ||
      item?.generic_product_name_en ||
      item?.generic_product_name ||
      ""
    );
  }, [item?.generic_product_id, item?.generic_product_name, item?.generic_product_name_el, item?.generic_product_name_en]);

  const loadGenericProducts = useCallback(async (query = "") => {
    setLoadingGenericProducts(true);
    setSelectionError("");
    try {
      const res = await api.shoppingList.getSelectionGenericProducts({
        q: query || undefined,
        lang: "en",
      });
      const nextProducts = res?.data?.items || [];
      setGenericProducts(nextProducts);
      return nextProducts;
    } catch (error) {
      setSelectionError("Failed to load generic products.");
      throw error;
    } finally {
      setLoadingGenericProducts(false);
    }
  }, []);

  const loadVariants = useCallback(async (genericProductId) => {
    if (!genericProductId) return [];
    setLoadingVariants(true);
    setSelectionError("");
    try {
      const res = await api.shoppingList.getSelectionVariants(genericProductId, {
        item_id: item?.id,
        qty_needed_base: item?.pricing_snapshot?.required_qty ?? item?.required_qty ?? item?.required_quantity ?? item?.qty_needed_base,
        base_unit: item?.pricing_snapshot?.required_unit ?? item?.required_unit ?? item?.base_unit ?? "g",
      });
      const nextVariants = res?.data?.items || [];
      setVariants(nextVariants);
      return nextVariants;
    } catch (error) {
      setSelectionError("Failed to load product variants.");
      throw error;
    } finally {
      setLoadingVariants(false);
    }
  }, [item]);

  const openSelectionModal = useCallback(async (preferredGenericProductId = null) => {
    setSelectionMode("genericThenVariants");
    setLockedGenericProductId(null);
    setLockedGenericProductName("");
    const products = await loadGenericProducts(searchQuery);
    const initialGenericId = preferredGenericProductId || item?.generic_product_id || products?.[0]?.id;
    if (initialGenericId) {
      setSelectedGenericId(initialGenericId);
      await loadVariants(initialGenericId);
    }
  }, [item?.generic_product_id, loadGenericProducts, loadVariants, searchQuery]);

  const openVariantsOnlyModal = useCallback(async ({ generic_product_id, generic_name } = {}) => {
    const lockedId = generic_product_id || item?.generic_product_id || null;
    setSelectionMode("variantsOnly");
    setLockedGenericProductId(lockedId);
    setLockedGenericProductName(
      generic_name ||
      item?.generic_product_name_el ||
      item?.generic_product_name_en ||
      item?.generic_product_name ||
      ""
    );
    if (lockedId) {
      setSelectedGenericId(lockedId);
    }
    setGenericProducts([]);
    setSearchQuery("");
    if (lockedId) {
      await loadVariants(lockedId);
    }
  }, [item?.generic_product_id, item?.generic_product_name, item?.generic_product_name_el, item?.generic_product_name_en, loadVariants]);

  const searchGenericProducts = useCallback(async (query) => {
    setSearchQuery(query);
    return loadGenericProducts(query);
  }, [loadGenericProducts]);

  const submitSelection = useCallback(async ({ variantId, genericProductId }) => {
    if (!item?.id || !variantId) return null;
    setSubmittingSelection(true);
    setSelectionError("");
    try {
      const res = await api.shoppingList.updateItemSelection(item.id, {
        variant_id: variantId,
        generic_product_id: genericProductId || selectedGenericId || item?.generic_product_id,
      });
      const updatedItem = res?.data?.item || res?.data?.updated_item || null;
      const matchedOption = variants.find(
        (option) => String(option.product_variant_id || option.id) === String(variantId)
      );
      const hydratedItem = updatedItem
        ? {
          ...updatedItem,
          selected_variant_id:
            updatedItem.selected_variant_id ??
            updatedItem.product_variant_id ??
            matchedOption?.product_variant_id ??
            matchedOption?.id ??
            variantId,
          selected_variant_snapshot: {
            ...(matchedOption
              ? {
                id: matchedOption.product_variant_id || matchedOption.id || variantId,
                brand_name: matchedOption.brand_name,
                brand_name_el: matchedOption.brand_name_el || matchedOption.brand_name,
                brand_name_en: matchedOption.brand_name_en || matchedOption.brand_name,
                store_name: matchedOption.store_name,
                store_chain: matchedOption.store_chain,
                package_size_text: matchedOption.package_size_text,
                price: matchedOption.price,
              }
              : {}),
            ...(updatedItem.selected_variant_snapshot || updatedItem.selected_variant || {}),
          },
          brand_name:
            updatedItem.brand_name ??
            updatedItem.selected_variant_snapshot?.brand_name ??
            updatedItem.selected_variant_snapshot?.brand_name_el ??
            matchedOption?.brand_name ??
            item.brand_name,
          store_name:
            updatedItem.store_name ??
            updatedItem.selected_variant_snapshot?.store_name ??
            matchedOption?.store_name ??
            item.store_name,
          store_chain:
            updatedItem.store_chain ??
            updatedItem.selected_variant_snapshot?.store_chain ??
            matchedOption?.store_chain ??
            item.store_chain,
        }
        : null;
      if (hydratedItem) {
        onItemUpdated?.(hydratedItem);
      }
      onSelectionSettled?.(hydratedItem);
      return hydratedItem;
    } catch (error) {
      setSelectionError(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to update selection."
      );
      throw error;
    } finally {
      setSubmittingSelection(false);
    }
  }, [item, onItemUpdated, onSelectionSettled, selectedGenericId, variants]);

  const labeledOptions = useMemo(
    () => variants.map((option) => ({ ...option, label: toOptionLabel(option, currencySymbol) })),
    [variants, currencySymbol]
  );

  return {
    genericProducts,
    variants: labeledOptions,
    loadGenericProducts,
    loadVariants,
    openSelectionModal,
    openVariantsOnlyModal,
    searchGenericProducts,
    submitSelection,
    loadingGenericProducts,
    loadingVariants,
    submittingSelection,
    selectionError,
    selectedVariantId,
    searchQuery,
    setSearchQuery,
    selectedGenericId,
    setSelectedGenericId,
    selectionMode,
    lockedGenericProductId,
    lockedGenericProductName,
  };
}
